// src/lib/metrics/trends.ts
/**
 * Trend analysis functions - Moving averages, streaks, ATH, momentum
 */

import type { Series } from './types';
import { filterValid, mean, stdDev } from './primitives';
import { toReturns } from './returns';
// Rolling functions imported when needed

// ============================================================================
// MOVING AVERAGE DISTANCE
// ============================================================================

/**
 * Calculate distance from simple moving average
 * Returns as decimal (0.05 = 5% above MA)
 */
export function maDistance(prices: Series, window: number): number | null {
  const valid = filterValid(prices);
  if (valid.length < window) return null;
  
  const current = valid[valid.length - 1]!;
  const ma = mean(valid.slice(-window));
  
  if (ma === null || ma === 0) return null;
  
  return (current - ma) / Math.abs(ma);
}

/**
 * Calculate distance from MA as percentage
 */
export function maDistancePercent(prices: Series, window: number): number | null {
  const dist = maDistance(prices, window);
  return dist !== null ? dist * 100 : null;
}

/**
 * Calculate multiple MA distances at once
 */
export function maDistances(
  prices: Series,
  windows: number[] = [20, 50, 200]
): Record<number, number | null> {
  const result: Record<number, number | null> = {};
  for (const w of windows) {
    result[w] = maDistance(prices, w);
  }
  return result;
}

/**
 * Calculate simple moving average (current value only)
 * Returns the average of the last `window` prices
 */
export function sma(prices: Series, window: number): number | null {
  const valid = filterValid(prices);
  if (valid.length < window) return null;
  return mean(valid.slice(-window));
}

/**
 * Calculate rolling SMA series
 * Returns SMA for each point in the series
 */
export function rollingMA(prices: Series, window: number): (number | null)[] {
  const result: (number | null)[] = [];
  
  for (let i = 0; i < prices.length; i++) {
    const start = Math.max(0, i - window + 1);
    const slice = prices.slice(start, i + 1);
    const valid = filterValid(slice);
    
    if (valid.length >= window * 0.5) { // Require at least 50% of window
      result.push(valid.reduce((a, b) => a + b, 0) / valid.length);
    } else {
      result.push(null);
    }
  }
  
  return result;
}

/**
 * Count periods where price is above its moving average
 */
export function periodsAboveMA(
  prices: Series,
  window: number
): { above: number; total: number } | null {
  const valid = filterValid(prices);
  if (valid.length < window) return null;
  
  let above = 0;
  let total = 0;
  
  for (let i = window - 1; i < valid.length; i++) {
    const ma = mean(valid.slice(i - window + 1, i + 1));
    if (ma !== null) {
      total++;
      if (valid[i]! > ma) {
        above++;
      }
    }
  }
  
  return { above, total };
}

/**
 * Count periods above MA within a specified range
 * Uses full price history to calculate MA, but only counts within the range
 * 
 * @param prices - Full price history
 * @param window - MA window size
 * @param rangeSize - Number of periods to count (from the end)
 */
export function periodsAboveMAInRange(
  prices: Series,
  window: number,
  rangeSize: number
): { above: number; total: number } | null {
  const valid = filterValid(prices);
  if (valid.length < window) return null;
  
  // Calculate rolling MA for full history
  const maValues: (number | null)[] = [];
  for (let i = 0; i < valid.length; i++) {
    if (i < window - 1) {
      maValues.push(null);
    } else {
      maValues.push(mean(valid.slice(i - window + 1, i + 1)));
    }
  }
  
  // Count only within the range (last N periods)
  const startIdx = Math.max(0, valid.length - rangeSize);
  let above = 0;
  let total = 0;
  
  for (let i = startIdx; i < valid.length; i++) {
    const ma = maValues[i];
    if (ma !== null && ma !== undefined) {
      total++;
      if (valid[i]! > ma) {
        above++;
      }
    }
  }
  
  // Return null if no valid comparisons in range
  if (total === 0) return null;
  
  return { above, total };
}

// ============================================================================
// ATH/ATL ANALYSIS
// ============================================================================

/**
 * Calculate distance from all-time high (or period high)
 * Returns negative percentage (e.g., -20 for 20% below ATH)
 */
export function distanceFromHigh(prices: Series): number | null {
  const valid = filterValid(prices);
  if (valid.length === 0) return null;
  
  const current = valid[valid.length - 1]!;
  const high = Math.max(...valid);
  
  if (high <= 0) return null;
  
  return ((current / high) - 1) * 100;
}

/**
 * Calculate days since all-time high (or period high)
 */
export function daysSinceHigh(prices: Series): number | null {
  const valid = filterValid(prices);
  if (valid.length === 0) return null;
  
  let maxIdx = 0;
  let maxVal = valid[0]!;
  
  for (let i = 1; i < valid.length; i++) {
    if (valid[i]! > maxVal) {
      maxVal = valid[i]!;
      maxIdx = i;
    }
  }
  
  return valid.length - 1 - maxIdx;
}

/**
 * Calculate distance from all-time low (or period low)
 * Returns positive percentage (e.g., 50 for 50% above ATL)
 */
export function distanceFromLow(prices: Series): number | null {
  const valid = filterValid(prices);
  if (valid.length === 0) return null;
  
  const current = valid[valid.length - 1]!;
  const low = Math.min(...valid);
  
  if (low <= 0) return null;
  
  return ((current / low) - 1) * 100;
}

// ============================================================================
// STREAK ANALYSIS
// ============================================================================

export interface StreakMetrics {
  /** Current consecutive up/down days (positive = up, negative = down) */
  currentStreak: number;
  /** Longest consecutive up days in the period */
  longestUpStreak: number;
  /** Longest consecutive down days in the period (positive number) */
  longestDownStreak: number;
}

/**
 * Calculate streak metrics from a price or return series
 */
export function calculateStreaks(prices: Series): StreakMetrics | null {
  const returns = toReturns(prices);
  const valid = filterValid(returns);
  
  if (valid.length === 0) {
    return null;
  }
  
  let currentStreak = 0;
  let longestUp = 0;
  let longestDown = 0;
  
  for (const r of valid) {
    if (r > 0) {
      currentStreak = currentStreak >= 0 ? currentStreak + 1 : 1;
      longestUp = Math.max(longestUp, currentStreak);
    } else if (r < 0) {
      currentStreak = currentStreak <= 0 ? currentStreak - 1 : -1;
      longestDown = Math.max(longestDown, Math.abs(currentStreak));
    }
    // On zero return, keep current streak (no change)
  }
  
  return {
    currentStreak,
    longestUpStreak: longestUp,
    longestDownStreak: longestDown,
  };
}

// ============================================================================
// MOMENTUM METRICS
// ============================================================================

export interface MomentumMetrics {
  /** Slope of log returns (trend direction and strength) */
  slope: number | null;
  /** Z-score of current price vs mean */
  zScore: number | null;
}

/**
 * Calculate momentum slope from returns (linear regression of log cumulative)
 * This measures the growth rate trend over the window
 * 
 * @param returns - Daily returns array
 * @param window - Number of periods to use (defaults to full array)
 */
export function momentumSlope(returns: Series, window?: number): number | null {
  const valid = filterValid(returns);
  if (valid.length < 5) return null;
  
  const recentReturns = window && window < valid.length 
    ? valid.slice(-window) 
    : valid;
  const n = recentReturns.length;
  
  // Build cumulative return series (indexed to 100)
  let cumulative = 100;
  const cumulativeSeries: number[] = [cumulative];
  
  for (let i = 0; i < n; i++) {
    const r = recentReturns[i];
    if (r !== undefined) {
      cumulative *= (1 + r);
      cumulativeSeries.push(cumulative);
    }
  }
  
  // Take log of cumulative series (growth rate)
  const logSeries = cumulativeSeries.slice(0, n).map(v => v > 0 ? Math.log(v) : 0);
  
  // Linear regression: y = mx + b, we want m (slope)
  const xMean = (n - 1) / 2;
  const yMean = logSeries.reduce((a, b) => a + b, 0) / n;
  
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < n; i++) {
    const y = logSeries[i] ?? 0;
    numerator += (i - xMean) * (y - yMean);
    denominator += (i - xMean) ** 2;
  }
  
  if (denominator === 0) return null;
  
  return numerator / denominator;
}

/**
 * Calculate momentum z-score (how unusual current momentum is vs history)
 * Uses rolling momentum values to calculate z-score
 * 
 * @param returns - Daily returns array
 * @param recentWindow - Window for recent momentum (default 30)
 * @param historyWindow - Minimum history required (default 180)
 */
export function momentumZScore(
  returns: Series, 
  recentWindow: number = 30,
  historyWindow: number = 180
): number | null {
  const valid = filterValid(returns);
  if (valid.length < historyWindow) return null;
  
  // Calculate recent momentum (cumulative return over recent window)
  const recentReturns = valid.slice(-recentWindow);
  const recentMomentum = recentReturns.reduce((acc, r) => acc * (1 + r), 1) - 1;
  
  // Calculate rolling momentum over all available history
  const rollingMomentums: number[] = [];
  
  for (let i = recentWindow; i <= valid.length; i++) {
    const windowReturns = valid.slice(i - recentWindow, i);
    const momentum = windowReturns.reduce((acc, r) => acc * (1 + r), 1) - 1;
    rollingMomentums.push(momentum);
  }
  
  if (rollingMomentums.length === 0) return null;
  
  // Calculate mean and std dev of historical momentum
  const m = mean(rollingMomentums);
  const s = stdDev(rollingMomentums);
  
  if (m === null || s === null || s === 0) return null;
  
  // Z-score = (current - mean) / stdDev
  return (recentMomentum - m) / s;
}

/**
 * Calculate momentum metrics (slope and z-score)
 */
export function calculateMomentum(prices: Series, window?: number): MomentumMetrics {
  const valid = filterValid(prices);
  if (valid.length < 5) return { slope: null, zScore: null };
  
  const windowPrices = window && window < valid.length 
    ? valid.slice(-window) 
    : valid;
  const n = windowPrices.length;
  
  // Build cumulative return series (indexed to 100)
  let cumulative = 100;
  const cumulativeSeries: number[] = [cumulative];
  
  for (let i = 1; i < n; i++) {
    const prev = windowPrices[i - 1]!;
    const curr = windowPrices[i]!;
    if (prev !== 0) {
      cumulative *= (curr / prev);
      cumulativeSeries.push(cumulative);
    }
  }
  
  // Take log of cumulative series
  const logSeries = cumulativeSeries.map(v => v > 0 ? Math.log(v) : 0);
  const logN = logSeries.length;
  
  // Linear regression: y = mx + b
  const xMean = (logN - 1) / 2;
  const yMean = mean(logSeries) ?? 0;
  
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < logN; i++) {
    numerator += (i - xMean) * ((logSeries[i] ?? 0) - yMean);
    denominator += (i - xMean) ** 2;
  }
  
  const slope = denominator !== 0 ? numerator / denominator : null;
  
  // Z-score
  const m = mean(valid);
  const s = stdDev(valid);
  const current = valid[valid.length - 1]!;
  const zScore = (m !== null && s !== null && s > 0) 
    ? (current - m) / s 
    : null;
  
  return { slope, zScore };
}

/**
 * Calculate Relative Strength Ratio (RS Ratio) vs benchmark
 * RS > 100 means outperforming, RS < 100 means underperforming
 */
export function relativeStrengthRatio(
  assetReturns: Series,
  benchmarkReturns: Series,
  window?: number
): number | null {
  const assetValid = filterValid(assetReturns);
  const benchValid = filterValid(benchmarkReturns);
  
  const n = Math.min(assetValid.length, benchValid.length);
  const windowSize = window ?? n;
  
  if (n < windowSize || windowSize < 2) return null;
  
  const assetSlice = assetValid.slice(-windowSize);
  const benchSlice = benchValid.slice(-windowSize);
  
  // Compound returns
  let assetCumulative = 1;
  let benchCumulative = 1;
  
  for (let i = 0; i < windowSize; i++) {
    const ar = assetSlice[i];
    const br = benchSlice[i];
    if (ar !== undefined && Number.isFinite(ar)) {
      assetCumulative *= (1 + ar);
    }
    if (br !== undefined && Number.isFinite(br)) {
      benchCumulative *= (1 + br);
    }
  }
  
  if (benchCumulative === 0) return null;
  
  return (assetCumulative / benchCumulative) * 100;
}

// ============================================================================
// RRG (RELATIVE ROTATION GRAPH) METRICS
// ============================================================================

export interface RRGMetrics {
  /** RS Ratio (x-axis in RRG) */
  rsRatio: number;
  /** RS Momentum (y-axis in RRG) */
  rsMomentum: number;
}

/**
 * Calculate RRG metrics for a single asset vs benchmark
 * Based on rolling RS Ratio calculations
 */
export function calculateRRGMetrics(
  assetPrices: Series,
  benchmarkPrices: Series,
  rsWindow: number = 10,
  momentumWindow: number = 10
): RRGMetrics | null {
  const assetValid = filterValid(assetPrices);
  const benchValid = filterValid(benchmarkPrices);
  
  const n = Math.min(assetValid.length, benchValid.length);
  if (n < rsWindow + momentumWindow) return null;
  
  // Calculate rolling RS ratios (asset / benchmark * 100)
  const rsRatios: number[] = [];
  
  for (let i = rsWindow; i <= n; i++) {
    const assetMA = mean(assetValid.slice(i - rsWindow, i));
    const benchMA = mean(benchValid.slice(i - rsWindow, i));
    
    if (assetMA !== null && benchMA !== null && benchMA !== 0) {
      rsRatios.push((assetMA / benchMA) * 100);
    }
  }
  
  if (rsRatios.length < momentumWindow) return null;
  
  // Normalize to rolling mean
  const rsNormalized: number[] = [];
  for (let i = momentumWindow; i <= rsRatios.length; i++) {
    const slice = rsRatios.slice(i - momentumWindow, i);
    const m = mean(slice);
    if (m !== null && m !== 0) {
      rsNormalized.push((rsRatios[i - 1]! / m) * 100);
    }
  }
  
  if (rsNormalized.length < 2) return null;
  
  // Current RS Ratio
  const rsRatio = rsNormalized[rsNormalized.length - 1]!;
  
  // RS Momentum (rate of change)
  const prevRS = rsNormalized[rsNormalized.length - 2]!;
  const rsMomentum = prevRS !== 0 ? ((rsRatio / prevRS) - 1) * 100 + 100 : 100;
  
  return { rsRatio, rsMomentum };
}

// ============================================================================
// WIN RATE & CAPTURE
// ============================================================================

export interface WinRateMetrics {
  /** Percentage of positive return days */
  winRate: number | null;
  /** Average return on up days */
  avgUp: number | null;
  /** Average return on down days */
  avgDown: number | null;
}

/**
 * Calculate win rate and average up/down returns
 */
export function calculateWinRate(returns: Series): WinRateMetrics {
  const valid = filterValid(returns);
  if (valid.length === 0) {
    return { winRate: null, avgUp: null, avgDown: null };
  }
  
  const ups = valid.filter(r => r > 0);
  const downs = valid.filter(r => r < 0);
  
  const winRate = (ups.length / valid.length) * 100;
  const avgUp = ups.length > 0 ? mean(ups) : null;
  const avgDown = downs.length > 0 ? mean(downs) : null;
  
  return { winRate, avgUp, avgDown };
}

/**
 * Calculate alpha hit rate (% of days outperforming benchmark)
 */
export function alphaHitRate(
  assetReturns: Series,
  benchmarkReturns: Series
): number | null {
  const assetValid = filterValid(assetReturns);
  const benchValid = filterValid(benchmarkReturns);
  
  const n = Math.min(assetValid.length, benchValid.length);
  if (n === 0) return null;
  
  let hits = 0;
  for (let i = 0; i < n; i++) {
    const excess = (assetValid[assetValid.length - n + i] ?? 0) - 
                   (benchValid[benchValid.length - n + i] ?? 0);
    if (excess > 0) hits++;
  }
  
  return (hits / n) * 100;
}

